import { useEffect, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  footer?: ReactNode;
}

const SIZE = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
  footer,
}: Props): ReactElement | null {
  // Cerrar con Escape. Bloquear scroll del body cuando está abierto.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-none" />

      {/* Card — bottom-sheet en mobile, dialog centrado en desktop */}
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative w-full bg-surface-elevated border border-surface-border shadow-2xl',
          'rounded-t-2xl sm:rounded-2xl',
          'flex flex-col max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)]',
          'sm:max-w-fit',
          'animate-modal-in',
          SIZE[size],
        )}
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }}
      >
        <header className="px-6 pt-5 pb-4 border-b border-surface-border">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-ink">{title}</h2>
              {description && (
                <p className="text-sm text-ink-muted mt-0.5">{description}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-ink-subtle hover:text-ink transition-colors -mt-1 -mr-1 p-1"
              aria-label="Cerrar"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </header>

        <div className="px-6 py-5 overflow-y-auto flex-1">{children}</div>

        {footer && (
          <footer className="px-6 py-4 border-t border-surface-border bg-surface-panel/30 rounded-b-2xl">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
